import React, { useEffect, useRef, useState, Ref, forwardRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useDisclosure } from '@mantine/hooks'
import {
  Avatar,
  Button,
  FileButton,
  Group,
  Image,
  Loader,
  Text,
  Textarea,
} from '@mantine/core'
import DOMPurify from 'dompurify'
import {
  IconEdit,
  IconMessageCircle2,
  IconTrash,
  IconUpload,
} from '@tabler/icons-react'
import { ArenaPost, useEditArenaPost } from './api'
import { useGetAsset, useUploadAsset } from '../assets/api'
import { LeaguestarState } from '../state'
import Comments from './Comments'
import DeletePost from './DeletePost'
import Reactions from '../socials/Reactions'
import classes from './Arena.module.css'
import clsx from 'clsx'

type AssetEditState =
  | { type: 'keep' }
  | { type: 'remove' }
  | { type: 'new'; asset_id: number; isVideo: boolean }

export default forwardRef(function PostDetails(
  {
    post_id,
    arenaPost,
    isEditing,
    editingPostId,
    setEditingPostId,
    initialCommentToScroll,
  }: {
    post_id: number
    arenaPost: ArenaPost
    isEditing: boolean
    editingPostId?: number
    setEditingPostId: React.Dispatch<React.SetStateAction<number | undefined>>
    initialCommentToScroll: number | null
  },
  ref: Ref<HTMLDivElement>
) {
  const { user_id } = LeaguestarState.useState((state) => ({
    user_id: state.user_id,
  }))

  const [
    openedDeleteModal,
    { open: openDeleteModal, close: closeDeleteModal },
  ] = useDisclosure(false)
  const [commentsOpen, setCommentsOpen] = useState<boolean>(false)
  const [file, setFile] = useState<File | null>(null)
  const resetRef = useRef<() => void>(null)
  const [assetEditState, setAssetEditState] = useState<AssetEditState>(
    arenaPost.image.main || arenaPost.video.main
      ? { type: 'keep' }
      : { type: 'remove' }
  )
  const queryClient = useQueryClient()
  const { mutate: addAsset, isPending: addAssetPending } = useUploadAsset()
  const { mutate: editArenaPost, isPending: editArenaPostPending } =
    useEditArenaPost()
  const newAssetId =
    assetEditState.type === 'new' ? assetEditState.asset_id : null
  const { data: asset, isFetching: assetIsFetching } = useGetAsset({
    asset_id: newAssetId,
  })
  const currentMediaType = arenaPost.image.main
    ? 'image'
    : arenaPost.video.main
      ? 'video'
      : null
  const [editingText, setEditingText] = useState<string>(arenaPost.text || '')
  const isEditingThisPost = editingPostId === arenaPost.post_id

  function clearFile() {
    setFile(null)
    resetRef.current?.()
    setAssetEditState({ type: 'remove' })
  }

  async function handleEditArenaPost() {
    const assetPayload =
      assetEditState.type === 'keep'
        ? {}
        : assetEditState.type === 'remove'
          ? { image_id: null as null, video_id: null as null }
          : {
              image_id: !assetEditState.isVideo
                ? assetEditState.asset_id
                : null,
              video_id: assetEditState.isVideo ? assetEditState.asset_id : null,
            }

    editArenaPost(
      {
        competition_id: arenaPost.competition_id,
        is_roar: arenaPost.is_roar,
        text: editingText,
        post_id: arenaPost.post_id,
        ...assetPayload,
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: ['arenaPosts'],
          })
          setFile(null)
          resetRef.current?.()
          setEditingPostId(undefined)
          setAssetEditState(
            assetEditState.type === 'remove'
              ? { type: 'remove' }
              : { type: 'keep' }
          )
        },
      }
    )
  }

  useEffect(() => {
    if (file) {
      const isVideo = file.type.startsWith('video/')
      const mediaType = isVideo ? 'video' : 'image'
      addAsset(
        {
          file: file,
          model_field: `social.Post.${mediaType}`,
        },
        {
          onSuccess: (uploadedAssetId: number) => {
            setAssetEditState({
              type: 'new',
              asset_id: uploadedAssetId,
              isVideo,
            })
          },
        }
      )
    }
  }, [file])

  useEffect(() => {
    if (isEditingThisPost) {
      setEditingText(arenaPost.text || '')
    }
  }, [isEditingThisPost, arenaPost.text])

  useEffect(() => {
    if (initialCommentToScroll) {
      setCommentsOpen(true)

      setTimeout(() => {
        const commentElement = document.getElementById(
          `comment-${initialCommentToScroll}`
        )
        if (commentElement) {
          commentElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 500)
    }
  }, [initialCommentToScroll])

  return (
    <>
      <div
        ref={ref}
        className={clsx(classes.postDetailsCard, {
          [classes.blurred]: isEditing && editingPostId !== arenaPost.post_id,
        })}
      >
        <div className={classes.postDetailsInfo}>
          <div className={classes.postAuthorDetails}>
            <Avatar
              className={classes.postAuthorAvatar}
              src={arenaPost.user_photo}
            />
            <div className={classes.authorDateContainer}>
              <p
                className={classes.postAuthor}
              >{`${arenaPost.user_first_name} ${arenaPost.user_last_name}`}</p>
              <p className={classes.date}>
                {new Date(arenaPost.created_at).toLocaleDateString('en-GB')}
              </p>
            </div>
          </div>
          {user_id === arenaPost.user_id && (
            <div className={classes.postActions}>
              <IconEdit
                className={classes.postDetailsIcon}
                onClick={() => {
                  if (isEditing) {
                    setAssetEditState(
                      arenaPost.image.main || arenaPost.video.main
                        ? { type: 'keep' }
                        : { type: 'remove' }
                    )
                    setFile(null)
                    resetRef.current?.()
                  }
                  setEditingPostId(isEditing ? undefined : arenaPost.post_id)
                }}
              />
              <IconTrash
                className={classes.postDetailsIcon}
                onClick={openDeleteModal}
              />
            </div>
          )}
        </div>

        {isEditingThisPost ? (
          <div className={classes.postDetailsText}>
            <Textarea
              classNames={{
                input: classes.textareaInput,
              }}
              value={editingText}
              onChange={(event) => setEditingText(event.currentTarget.value)}
              placeholder="What's happening?"
              maxLength={arenaPost.is_roar ? 50 : 280}
              autosize
              minRows={2}
            />
            <Text size="sm" c="dimmed" ta="right" mt={4}>
              {editingText.length} / {arenaPost.is_roar ? 50 : 280}
            </Text>
          </div>
        ) : (
          arenaPost.text && (
            <div className={classes.postDetailsText}>
              <Text
                component="p"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(arenaPost.text),
                }}
              />
            </div>
          )
        )}

        {(assetEditState.type === 'keep' || !isEditingThisPost) &&
          (arenaPost.image.main || arenaPost.video.main) && (
            <div className={classes.assetContainer}>
              {currentMediaType === 'image' && (
                <Image
                  src={arenaPost.image.main}
                  className={classes.postAsset}
                />
              )}
              {currentMediaType === 'video' && (
                <video
                  src={arenaPost.video.main}
                  controls
                  className={classes.postAsset}
                />
              )}
              {isEditingThisPost && (
                <div className={classes.editAsset}>
                  <IconTrash
                    className={classes.removeImageIcon}
                    onClick={() => setAssetEditState({ type: 'remove' })}
                  />
                </div>
              )}
            </div>
          )}

        {isEditingThisPost && assetEditState.type !== 'keep' && (
          <div className={classes.assetContainer}>
            {asset && 'main' in asset && 'url' in asset.main ? (
              <>
                {assetEditState.type === 'new' && assetEditState.isVideo ? (
                  <video
                    src={asset.main.url}
                    controls
                    className={classes.postAsset}
                  />
                ) : (
                  <Image src={asset.main.url} className={classes.postAsset} />
                )}
                <div className={classes.editAsset}>
                  <IconTrash
                    className={classes.removeImageIcon}
                    onClick={clearFile}
                  />
                </div>
              </>
            ) : (
              <div className={classes.addAssetContainer}>
                {editArenaPostPending ||
                addAssetPending ||
                assetIsFetching ||
                (asset && 'main' in asset && 'task_id' in asset.main) ? (
                  <Group>
                    <Loader color="#0B6B7B" />
                    <Text className={classes.loaderText}>
                      Your file is uploading...
                    </Text>
                  </Group>
                ) : (
                  <FileButton
                    onChange={setFile}
                    accept="image/png,image/jpeg,image/jpg,video/mp4,video/mov,video/avi,video/webm"
                  >
                    {(props) => (
                      <Button
                        {...props}
                        className={classes.editButton}
                        leftSection={
                          <IconUpload width={16} height={16} color="white" />
                        }
                      >
                        Upload image / video
                      </Button>
                    )}
                  </FileButton>
                )}
              </div>
            )}
          </div>
        )}

        {isEditingThisPost && (
          <Group w="100%" justify="flex-end" pr={8}>
            <Button
              className={classes.editButton}
              onClick={handleEditArenaPost}
            >
              Save
            </Button>
          </Group>
        )}

        <div className={classes.postDetailsSocials}>
          <div
            className={classes.postComments}
            onClick={() => setCommentsOpen(!commentsOpen)}
          >
            <IconMessageCircle2 className={classes.socialsIcon} />
            {arenaPost.comment_count > 0 && (
              <Text component="p">{arenaPost.comment_count}</Text>
            )}
          </div>
          <Reactions
            post_id={arenaPost.post_id}
            reactions={arenaPost.reactions}
            isRoar={arenaPost.is_roar}
          />
        </div>
        {commentsOpen && <Comments post_id={arenaPost.post_id} />}
      </div>

      <DeletePost
        post_id={post_id}
        openedDeleteModal={openedDeleteModal}
        closeDeleteModal={closeDeleteModal}
      />
    </>
  )
})
